import type {
  AnalyticsEventProperties,
  AnalyticsIdentity,
  AnalyticsTrackOptions,
  AnalyticsWorkspace,
  AuthSessionResponse,
  BrowserProductAnalytics,
  ProductAnalyticsEventName,
} from "@yagni/shared";
import { BrowserMixpanelProvider } from "./mixpanelProvider.js";
import { NoopBrowserProvider } from "./noopProvider.js";
import { normalizePath } from "./pathNormalizer.js";

const TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
const ENV = (import.meta.env.VITE_MIXPANEL_ENV as string | undefined) ?? "development";
const EXPLICIT = (
  import.meta.env.VITE_PRODUCT_ANALYTICS_PROVIDER as string | undefined
)?.trim().toLowerCase();

function resolveProvider(): BrowserProductAnalytics {
  if (EXPLICIT === "noop") return new NoopBrowserProvider();
  if (EXPLICIT === "mixpanel" && !TOKEN) {
    console.warn(
      "VITE_PRODUCT_ANALYTICS_PROVIDER=mixpanel but VITE_MIXPANEL_TOKEN is unset; using noop"
    );
    return new NoopBrowserProvider();
  }
  if (TOKEN) return new BrowserMixpanelProvider({ token: TOKEN, environment: ENV });
  return new NoopBrowserProvider();
}

const provider: BrowserProductAnalytics = resolveProvider();

export function identifyUser(session: AuthSessionResponse): void {
  const role =
    session.memberships.find((m) => m.workspace.id === session.workspace.id)?.role ??
    "member";
  const identity: AnalyticsIdentity = {
    userId: session.user.id,
    email: session.user.email,
    displayName: session.user.displayName,
    createdAt: session.user.createdAt,
    isInternal: session.isInternal === true,
  };
  // `plan` is deliberately omitted: the session payload doesn't carry it, and
  // writing a placeholder would overwrite the real plan trait the backend
  // billing pipeline maintains (profile traits are last-write-wins).
  const workspace: AnalyticsWorkspace = {
    workspaceId: session.workspace.id,
    slug: session.workspace.slug,
    name: session.workspace.name,
    createdAt: session.workspace.createdAt,
  };
  provider.identify(identity, workspace);
  trackClientEvent("Page Viewed", {
    path: normalizePath(window.location.pathname + window.location.search),
    role,
  });
}

export function trackClientEvent<E extends ProductAnalyticsEventName>(
  name: E,
  props: AnalyticsEventProperties<E>,
  options?: AnalyticsTrackOptions
): void {
  provider.track(name, props, options);
}

export function trackPageView(path: string): void {
  provider.track("Page Viewed", { path: normalizePath(path) });
}

export function resetUser(): void {
  provider.reset();
}

export { normalizePath };
